import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '@/contexts/AuthContext';
import { Loader2, AlertTriangle } from 'lucide-react';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        const hash = window.location.hash;
        const sessionId = hash.split('session_id=')[1]?.split('&')[0];

        if (!sessionId) {
          setError('No session ID returned from Google. Please try again.');
          setTimeout(() => navigate('/auth', { replace: true }), 3000);
          return;
        }

        const res = await api.post('/auth/google/session', { session_id: sessionId });
        const data = res.data;
        login(data, data.session_token);

        // Clear the hash
        window.history.replaceState(null, '', window.location.pathname);

        if (data.profile_complete) {
          navigate('/discover', { replace: true, state: { user: data } });
        } else {
          navigate('/profile/setup', { replace: true, state: { user: data } });
        }
      } catch (err) {
        let msg;
        if (err.response?.data?.detail) msg = err.response.data.detail;
        else if (err.response?.status === 401) msg = 'Google session expired. Please sign in again.';
        else if (err.response?.status === 500) msg = 'Server error. Please try again in a moment.';
        else msg = err.message || 'Unknown error';
        console.error('Auth callback error:', err.response || err);
        setError(`${msg} (${err.response?.status || 'network'})`);
        setTimeout(() => navigate('/auth', { replace: true }), 4000);
      }
    };

    processSession();
  }, [navigate, login]);

  if (error) return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6" data-testid="auth-callback-error">
      <div className="text-center max-w-sm">
        <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-6 h-6 text-red-600" />
        </div>
        <h2 className="text-lg font-bold mb-2 text-[#0F0F10]">Sign-in failed</h2>
        <p className="text-sm text-[#4B4B52] mb-6 leading-relaxed">{error}</p>
        <button onClick={() => navigate('/auth', { replace: true })}
          className="brand-gradient text-white px-6 py-3 rounded-full text-sm font-semibold brand-glow-sm">
          Try again
        </button>
        <p className="text-xs text-[#8A8A93] mt-6">Redirecting automatically…</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#B91C1C] mx-auto mb-4" />
        <p className="text-[#4B4B52]">Signing you in...</p>
      </div>
    </div>
  );
}
